import { Flex } from '@chakra-ui/layout';
import { FormControl, Input } from '@chakra-ui/react';
import React, { useEffect } from 'react';
import {
  useFormContext,
  Controller,
  RegisterOptions,
  useWatch,
} from 'react-hook-form';

import { Select } from '~/packages/common-frontend/components/Select';
import { Typography } from '~/packages/common-frontend/components/Typography';
import {
  SharedInputLabel,
  sharedSectionStyles,
} from '~/packages/funding-requests/ui/submit/CreateRequest/Shared';

export const TextInput = ({
  name,
  placeholder = '',
  defaultValue = '',
  hintText,
  rules,
}: {
  name: string;
  placeholder?: string;
  defaultValue?: string;
  hintText?: string;
  rules?: RegisterOptions;
}) => {
  const { control } = useFormContext();

  return (
    <Flex direction="column" w="100%" gap="0.5rem">
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        rules={rules}
        render={({ field, fieldState: { error } }) => (
          <>
            <Input
              type="text"
              placeholder={placeholder}
              {...field}
              errorBorderColor={error?.message ? 'red.400' : undefined}
            />
            {hintText && !error?.message && (
              <Typography variant="detail-muted">{hintText}</Typography>
            )}
            {error?.message && (
              <Typography
                variant="detail-muted"
                TextProps={{ color: 'red.400' }}
              >
                {error.message}
              </Typography>
            )}
          </>
        )}
      />
    </Flex>
  );
};

export const SelectInput = ({
  name,
  options,
  placeholder = '',
}: {
  name: string;
  options: { label: React.ReactNode; value: string }[];
  placeholder?: string;
}) => {
  const { control, register, unregister, setValue } = useFormContext();
  const value = useWatch({ control, name });

  useEffect(() => {
    register(name);

    return () => {
      unregister(name);
    };
  }, [name, register, unregister]);

  return (
    <Select
      placeholderLabel={placeholder}
      value={value}
      onChange={(nextValue) => {
        setValue(name, nextValue);
      }}
      options={options}
    />
  );
};

export const CurrencyInput = ({
  name,
  placeholder = '',
  defaultValue = '',
  hintText,
  rules,
  showErrorMessage = true,
}: {
  name: string;
  placeholder?: string;
  defaultValue?: string;
  hintText?: string;
  rules?: RegisterOptions;
  showErrorMessage?: boolean;
}) => {
  const { control } = useFormContext();

  return (
    <Flex direction="column" w="100%" gap="0.5rem">
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        rules={rules}
        render={({ field, fieldState: { error } }) => {
          return (
            <>
              <Input
                type="number"
                placeholder={placeholder}
                {...field}
                isInvalid={Boolean(error)}
              />
              {hintText && !error?.message && (
                <Typography variant="detail-muted">{hintText}</Typography>
              )}
              {showErrorMessage && error?.message && (
                <Typography
                  variant="detail-muted"
                  TextProps={{ color: 'red.400' }}
                >
                  {error.message}
                </Typography>
              )}
            </>
          );
        }}
      />
    </Flex>
  );
};

export const InputContainer = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <FormControl as={Flex} {...sharedSectionStyles}>
    <SharedInputLabel label={label} />
    {children}
  </FormControl>
);
